import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Modal, message } from 'antd';
import styled from 'styled-components/macro';
import SchoolLayout from '../../layout/SchoolLayoutWithTabs';
import SchedulePreviewTable from './SchedulePreviewTable';
import {
  ArrearsDecisionSummary, DecisionPreview, MutationResult, RecoveryPlan,
  approveDecision, declineDecision, fetchDecision, previewDecision, reviewDecision,
} from '../../../../feature/arrearsDecisions';
import { buildRecoveryRows, isIsoDate, recoveryTotal, spreadPlan } from '../../../../utils/recoverySchedule';
import ROUTER_CONFIG from '../../../../router/router-config';

const money = (n: number) => `£${n.toFixed(2)}`;

const defaultFirstDate = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 15);
  return d.toISOString().slice(0, 10);
};

export default function ArrearsDecisionReview() {
  const { campaignId, decisionId } = useParams<{ campaignId: string; decisionId: string }>();
  const [decision, setDecision] = useState<ArrearsDecisionSummary | null>(null);
  const [preview, setPreview] = useState<DecisionPreview | null>(null);
  const [plan, setPlan] = useState<RecoveryPlan | null>(null);
  const [previewFresh, setPreviewFresh] = useState(true);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const previewSeq = useRef(0);

  useEffect(() => {
    fetchDecision(decisionId).then(({ decision: d, preview: p }) => {
      setDecision(d);
      setPreview(p);
      setPlan(p.plan);
      setPreviewFresh(true);
    }).catch(() => message.error('Could not load this decision.'));
  }, [decisionId]);

  const usualMonths = useMemo(() => (preview ? preview.rows.filter((r) => r.usual > 0).map((r) => r.date.slice(0, 7)) : []), [preview]);
  const localRows = useMemo(
    () => (plan && preview && decision ? buildRecoveryRows(plan, preview.students, decision.usualAmount, usualMonths) : []),
    [plan, preview, decision, usualMonths],
  );
  // The server preview is authoritative; local rows only bridge the gap while a preview request is in flight.
  const rows = previewFresh && preview ? preview.rows : localRows;
  const total = recoveryTotal(rows);
  const validDate = !!plan && isIsoDate(plan.firstCollectionDate);
  const totalMismatch = preview ? Math.abs(total - preview.liveArrears) > 0.01 : true;
  const tooSmall = rows.some((r) => r.extra < 0.01);
  const canApprove = !!(preview && plan && validDate && previewFresh && preview.sendable && preview.liveArrears > 0
    && !totalMismatch && !tooSmall && rows.length > 0 && decision?.status === 'pending');

  const refreshPreview = (next: RecoveryPlan) => {
    // Bump the sequence first so an in-flight answer for a previous edit is dropped even when this edit is invalid.
    const seq = previewSeq.current + 1;
    previewSeq.current = seq;
    setPreviewFresh(false);
    if (!isIsoDate(next.firstCollectionDate)) return;
    previewDecision(decisionId, next)
      .then((p) => {
        if (previewSeq.current !== seq) return;
        setPreview(p);
        // The server normalises the plan (moves past dates, forces monthly); what is shown is what gets approved.
        if (p.plan) setPlan(p.plan);
        setPreviewFresh(true);
      })
      .catch(() => { if (previewSeq.current === seq) message.error('Could not refresh the preview.'); });
  };

  // Every edit keeps the schedule total equal to the live arrears, spread in whole pennies.
  const updatePlan = (patch: Partial<RecoveryPlan>) => {
    if (!plan || !preview) return;
    let next: RecoveryPlan = { ...plan, ...patch };
    const perStudentArrears = preview.liveArrears / Math.max(preview.students, 1);
    if (patch.noPayments !== undefined) {
      next = { ...next, ...spreadPlan(perStudentArrears, next.noPayments) };
    } else if (patch.regularTake !== undefined) {
      const regular = Math.max(0, next.regularTake);
      const count = regular > 0 ? Math.ceil(perStudentArrears / regular - 1e-9) : 1;
      next = { ...next, ...spreadPlan(perStudentArrears, count) };
    }
    setPlan(next);
    refreshPreview(next);
  };

  const proposePlan = () => {
    if (!preview) return;
    const perStudentArrears = preview.liveArrears / Math.max(preview.students, 1);
    const next: RecoveryPlan = { period: 'monthly', firstCollectionDate: defaultFirstDate(), ...spreadPlan(perStudentArrears, 4) };
    setPlan(next);
    refreshPreview(next);
  };

  const runMutation = async (call: () => Promise<MutationResult>, onOk: (r: MutationResult) => void, verb: string) => {
    setBusy(true);
    try {
      const result = await call();
      if (!result) throw new Error('empty response');
      if (result.error === 'not_pending') {
        setDecision(result.decision);
        setOutcome(`Already ${result.decision.status} by ${result.decision.decidedBy || 'a colleague'}.`);
      } else if (result.error) {
        message.error(`Could not ${verb}: ${result.error}`);
      } else {
        onOk(result);
      }
    } catch {
      message.error(`Could not ${verb}. Please try again.`);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setDeclineOpen(false);
    }
  };

  const onApprove = () => plan && runMutation(() => approveDecision(decisionId, plan), (result) => {
    setDecision(result.decision);
    setOutcome(`Recovery plan created (subscription #${result.recoverySubscriptionId}). It will be sent to the bank in the next weekly run.`);
  }, 'approve');

  const onDecline = () => runMutation(() => declineDecision(decisionId, note), (result) => {
    setDecision(result.decision);
    setOutcome('Declined. The parent has been emailed your message.');
  }, 'decline');

  const onReview = () => runMutation(() => reviewDecision(decisionId, note), (result) => {
    setDecision(result.decision);
    setOutcome('Marked as reviewed.');
  }, 'update');

  if (!decision || !preview) return <SchoolLayout><Wrap>Loading…</Wrap></SchoolLayout>;

  const pending = decision.status === 'pending';

  return (
    <SchoolLayout>
      <Wrap>
        <BackLink to={ROUTER_CONFIG.SCHOOL_CAMPAIGN_INFO.getPath({ campaignId })}>‹ Back to scheme</BackLink>
        <h1>Arrears decision: {decision.parentName}</h1>

        {outcome && <Banner tone="ok">{outcome}</Banner>}
        {preview.stale && (
          <Banner tone="warn">
            The arrears have changed since the parent replied (was {money(preview.snapshotArrears)}, now {money(preview.liveArrears)}). The plan below uses the current figure.
          </Banner>
        )}
        {preview.dateAdjusted && (
          <Banner tone="warn">The requested first collection date had passed, so it has been moved to the next available date.</Banner>
        )}
        {!preview.sendable && (
          <Banner tone="bad">This parent cannot be sent to the bank (no bank reference or Direct Debit unhooked). Fix their details first.</Banner>
        )}

        <Section>
          <h2>Situation</h2>
          <p>Students: {decision.studentCodes.join(', ') || '—'}. Usual plan: {money(decision.usualAmount)} {decision.usualPeriod}, {decision.collectionsDone} of {decision.collectionsPlanned} collections made.</p>
          <p>Currently in arrears of <strong>{money(preview.liveArrears)}</strong>.</p>
          <p>Parent chose: <strong>{decision.optionLabel}</strong>{decision.parentMessage && <Quote>“{decision.parentMessage}”</Quote>}</p>
        </Section>

        {!plan && pending && preview.liveArrears > 0 && (
          <Section>
            <h2>No plan yet</h2>
            <p>The parent asked the school for an alternative. Propose a repayment schedule and adjust it below before approving.</p>
            <Secondary onClick={proposePlan}>Propose a plan</Secondary>
          </Section>
        )}

        {plan && (
          <Section>
            <h2>What will happen</h2>
            {rows.length > 0 ? <SchedulePreviewTable rows={rows} /> : <p>Enter a complete first collection date to see the schedule.</p>}
            <Summary>
              Total recovered <strong>{money(total)}</strong> over {rows.length} extra collection{rows.length === 1 ? '' : 's'}
              {rows.length > 0 && <>, from {rows[0].date} to {rows[rows.length - 1].date}</>}. Original plan continues unchanged.
              The bank may move a collection to the next available working day; amounts do not change.
              {totalMismatch && rows.length > 0 && <Mismatch> Total must equal the arrears ({money(preview.liveArrears)}).</Mismatch>}
              {tooSmall && <Mismatch> Every collection must be at least £0.01.</Mismatch>}
              {!previewFresh && validDate && <Pending> Updating preview…</Pending>}
            </Summary>
            <h3>Adjust</h3>
            <Adjust>
              <label>
                Extra per collection
                <input type="number" step="0.01" min="0" value={plan.regularTake} aria-label="Extra per collection" onChange={(e) => updatePlan({ regularTake: Number(e.target.value) })} />
              </label>
              <label>
                Number of collections
                <input type="number" min="1" value={plan.noPayments} aria-label="Number of collections" onChange={(e) => updatePlan({ noPayments: Number(e.target.value) })} />
              </label>
              <label>
                First collection date
                <input type="date" value={plan.firstCollectionDate} aria-label="First collection date" onChange={(e) => updatePlan({ firstCollectionDate: e.target.value })} />
              </label>
            </Adjust>
          </Section>
        )}

        {pending && (
          <Actions>
            {plan && <Primary disabled={!canApprove || busy} onClick={() => setConfirmOpen(true)}>Approve</Primary>}
            <Secondary disabled={busy} onClick={() => setDeclineOpen(true)}>Decline</Secondary>
            {decision.option === 'wrong_info' && <Secondary disabled={busy} onClick={onReview}>Mark as reviewed</Secondary>}
          </Actions>
        )}

        <Modal open={confirmOpen} onCancel={() => setConfirmOpen(false)} onOk={onApprove} okText="Confirm" okButtonProps={{ loading: busy }} title="Approve recovery plan">
          <p>{money(total)} over {rows.length} collection{rows.length === 1 ? '' : 's'} starting {rows[0]?.date}. The parent will be emailed the schedule.</p>
        </Modal>

        <Modal open={declineOpen} onCancel={() => setDeclineOpen(false)} onOk={onDecline} okText="Send decline" okButtonProps={{ disabled: note.trim() === '', loading: busy }} title="Decline">
          <p>This message is emailed to the parent.</p>
          <textarea aria-label="Message to the parent" rows={4} style={{ width: '100%' }} value={note} onChange={(e) => setNote(e.target.value)} />
        </Modal>
      </Wrap>
    </SchoolLayout>
  );
}

const Wrap = styled.main`
  padding: 24px 0 80px;
  max-width: 900px;
  h1 { font-size: 24px; }
  h2 { font-size: 18px; margin: 0 0 12px; }
  h3 { font-size: 15px; margin: 20px 0 8px; }
`;
const BackLink = styled(Link)`color: #2682DE;`;
const Section = styled.section`margin: 24px 0; padding: 20px; background: #F7F9FB; border-radius: 8px;`;
const Quote = styled.span`display: block; margin-top: 6px; font-style: italic; color: #6B7A87;`;
const Summary = styled.p`margin-top: 12px;`;
const Mismatch = styled.span`color: #B91C1C;`;
const Pending = styled.span`color: #6B7A87;`;
const Adjust = styled.div`
  display: flex; gap: 16px; flex-wrap: wrap;
  label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #6B7A87; }
  input { padding: 6px 8px; border: 1px solid #E0E0E0; border-radius: 4px; min-width: 160px; }
`;
const Actions = styled.div`display: flex; gap: 12px; flex-wrap: wrap;`;
const Primary = styled.button`
  padding: 10px 28px; background: #2682DE; color: #fff; border: 0; border-radius: 4px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Secondary = styled.button`
  padding: 10px 20px; background: #fff; color: #1F1F1F; border: 1px solid #E0E0E0; border-radius: 4px; cursor: pointer;
  &:disabled { opacity: .5; }
`;
const Banner = styled.div<{ tone: 'ok' | 'warn' | 'bad' }>`
  padding: 12px 16px; border-radius: 6px; margin: 12px 0;
  background: ${(p) => (p.tone === 'ok' ? '#ECFDF3' : p.tone === 'warn' ? '#FFF8EC' : '#FEF2F2')};
  border: 1px solid ${(p) => (p.tone === 'ok' ? '#6EE7B7' : p.tone === 'warn' ? '#F5C36B' : '#FCA5A5')};
`;
